import type { ReactNode } from 'react'
import { useTranslation } from 'react-i18next'
import { Button, Spinner, type SubscriptionStatus } from '@structural-climbing/ui'
import type { ActiveCheckInEntry } from './memberTypes'
import type { CheckInState } from '../checkIn/useCheckIn'
import { CheckMark } from '../checkIn/CheckMark'

interface Props {
  /** The account being checked in. For a child, this is still the parent's id. */
  userId: string
  /** Set for a child row; the check-in is recorded against the child. */
  childId?: string
  /** The subscription that authorises this visit — the child's own for a child. */
  subStatus: SubscriptionStatus | undefined
  checkInState: CheckInState
  /** The open check-in for this person, if they are in the gym right now. */
  activeEntry: ActiveCheckInEntry | undefined
  isCheckingOut: (checkInId: string) => boolean
  /** True once a check-out came back 409 — someone else got there first. */
  isAlreadyCheckedOut?: (checkInId: string) => boolean
  onCheckIn: (userId: string, childId?: string) => void
  onCheckOut: (checkInId: string, isGuest: boolean) => void
  /**
   * Shown instead of a disabled Check-in when the plan does not let them in,
   * e.g. "Renew plan". Pass null to show nothing. Left out, the disabled
   * button stays.
   */
  inactiveAction?: ReactNode
}

/**
 * The check-in / check-out control for one person in a list.
 *
 * Shared by the gym-wide members page and a trainer's own list. It was two
 * near-identical copies inside MembersPage — one for adults, one for children —
 * and the child copy had silently lost the already-checked-out branch, so a
 * child checked out from another tab showed a live "Check out" button that
 * failed. Passing `childId` is the only difference between the two now.
 */
export function CheckInActions({
  userId,
  childId,
  subStatus,
  checkInState,
  activeEntry,
  isCheckingOut,
  isAlreadyCheckedOut,
  onCheckIn,
  onCheckOut,
  inactiveAction,
}: Props) {
  const { t } = useTranslation()
  const alreadyIn = checkInState === 'already_in' || !!activeEntry

  if (checkInState === 'loading') {
    return (
      <Button size="sm" variant="flat" color="primary" isDisabled>
        <Spinner size="sm" color="current" />
      </Button>
    )
  }

  if (checkInState === 'success') {
    return (
      <span className="rounded-full bg-success/10 px-2 py-0.5 text-xs font-medium text-success">
        {t('members.checkInSuccess')}
      </span>
    )
  }

  if (alreadyIn && activeEntry) {
    if (isAlreadyCheckedOut?.(activeEntry.checkInId)) {
      return (
        <span className="rounded-full bg-warning/10 px-2 py-0.5 text-xs font-medium text-warning">
          {t('dashboard.alreadyCheckedOut')}
        </span>
      )
    }
    return (
      <Button
        size="sm"
        variant="bordered"
        className="border-1"
        isDisabled={isCheckingOut(activeEntry.checkInId)}
        onClick={() => onCheckOut(activeEntry.checkInId, activeEntry.isGuest)}
      >
        {isCheckingOut(activeEntry.checkInId)
          ? t('dashboard.checkingOut')
          : t('dashboard.checkOut')}
      </Button>
    )
  }

  if (alreadyIn) {
    return (
      <Button size="sm" variant="flat" isDisabled>
        {t('members.checkInAlreadyIn')}
      </Button>
    )
  }

  if (subStatus !== 'active' && inactiveAction !== undefined) return <>{inactiveAction}</>

  return (
    <Button
      size="sm"
      variant={subStatus === 'active' ? 'solid' : 'flat'}
      color="primary"
      isDisabled={subStatus !== 'active'}
      onClick={() => onCheckIn(userId, childId)}
      startContent={subStatus === 'active' ? <CheckMark /> : undefined}
    >
      {t('members.manualCheckIn')}
    </Button>
  )
}
